/**
 * Credit top-up cards — prepaid packs for the WhatsApp and AI wallets
 * (centralized credit subsystem, ADR-0033).
 *
 * Owner picks a pack → `billing.createTopupOrder` creates a Razorpay order →
 * Razorpay Checkout opens → the customer pays → the Razorpay webhook credits the
 * wallet (server-truth; the client just refreshes). When Razorpay isn't
 * configured (e.g. local dev), the order call returns PRECONDITION and we show a
 * "talk to us" message instead of a broken checkout.
 *
 * Pricing is a PLACEHOLDER (defers exact pricing) — see WA_TOPUP_PACKS / AI_TOPUP_PACKS.
 */
import * as React from "react";
import { MessageSquare, Sparkles, Check } from "lucide-react";
import { cn, toast } from "@flint/ui";
import { WA_TOPUP_PACKS, AI_TOPUP_PACKS, type TopupPack } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.ts";
import { usePermission } from "../../../lib/permissions.ts";
import { FormCard } from "../form-card.tsx";
import { openCheckout } from "./razorpay.ts";

function rupees(paise: number): string {
  return `₹${(paise / 100).toLocaleString("en-IN")}`;
}

/** Generic top-up card — one wallet, its packs, its copy. */
function TopupCard({
  packs,
  title,
  description,
  unitLabel,
  icon,
}: {
  packs: readonly TopupPack[];
  title: string;
  description: string;
  /** e.g. "WhatsApp credits" / "AI credits" — used in checkout + toast copy. */
  unitLabel: string;
  icon: React.ReactNode;
}) {
  const canManage = usePermission("billing.manage");
  const [busyPack, setBusyPack] = React.useState<string | null>(null);
  const utils = trpc.useUtils();
  const createOrder = trpc.billing.createTopupOrder.useMutation();

  if (!canManage) return null;

  async function buy(pack: TopupPack) {
    setBusyPack(pack.id);
    try {
      const order = await createOrder.mutateAsync({ packId: pack.id });
      const opened = await openCheckout({
        key: order.razorpayKeyId,
        order_id: order.orderId,
        amount: order.amountPaise,
        currency: order.currency,
        name: "Flint",
        description: `${pack.credits.toLocaleString("en-IN")} ${unitLabel}`,
        handler: () => {
          toast.success("Payment received", {
            description: `Your ${unitLabel} will appear in a moment.`,
          });
          // Crediting is server-side via the webhook; refetch shortly after.
          setTimeout(() => void utils.billing.overview.invalidate(), 2500);
        },
      });
      if (!opened) {
        toast.error("Couldn't open checkout", { description: "Please retry, or talk to us to add credits." });
        return;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Top-up failed";
      toast.error("Top-up unavailable", { description: msg });
    } finally {
      setBusyPack(null);
    }
  }

  return (
    <FormCard icon={icon} title={title} description={description} collapsible>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {packs.map((p) => (
          <div
            key={p.id}
            className={cn(
              "flex flex-col rounded-md border bg-paper p-4 shadow-card",
              p.badge === "Best value" ? "border-sky-600 ring-1 ring-sky-100" : "border-line",
            )}
          >
            <div className="mb-1 flex h-5 items-center">
              {p.badge ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700 ring-1 ring-sky-100">
                  {p.badge === "Best value" ? <Check size={11} aria-hidden /> : null}
                  {p.badge}
                </span>
              ) : null}
            </div>
            <p className="text-lg font-bold tabular-nums text-ink">{p.credits.toLocaleString("en-IN")}</p>
            <p className="text-xs text-muted">credits</p>
            <p className="mt-2 text-sm font-semibold text-ink">{rupees(p.pricePaise)}</p>
            <button
              type="button"
              onClick={() => void buy(p)}
              disabled={busyPack !== null}
              className="mt-3 inline-flex h-9 items-center justify-center rounded-sm bg-ink px-3 text-sm font-semibold text-paper hover:bg-ink-2 disabled:opacity-60"
            >
              {busyPack === p.id ? "Opening…" : "Top up"}
            </button>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-muted">
        Secure payment via Razorpay. GST included. Credits are added once payment is confirmed.
      </p>
    </FormCard>
  );
}

/** WhatsApp credit top-up — only relevant when the WhatsApp Business add-on is enabled. */
export function WaTopup({ enabled }: { enabled: boolean }) {
  if (!enabled) return null;
  return (
    <TopupCard
      packs={WA_TOPUP_PACKS}
      title="Top up WhatsApp credits"
      description="Buy a prepaid pack anytime — credits never expire while your plan is active."
      unitLabel="WhatsApp credits"
      icon={<MessageSquare size={14} strokeWidth={2} aria-hidden />}
    />
  );
}

/** AI credit top-up — only relevant when the Flint AI add-on is enabled. */
export function AiTopup({ enabled }: { enabled: boolean }) {
  if (!enabled) return null;
  return (
    <TopupCard
      packs={AI_TOPUP_PACKS}
      title="Top up AI credits"
      description="Out of monthly AI credits? Buy a prepaid pack to keep Ask Flint, briefings, and extraction running."
      unitLabel="AI credits"
      icon={<Sparkles size={14} strokeWidth={2} aria-hidden />}
    />
  );
}
